import {
	unstable_AdapterInterface as AdapterInterface,
	type unstable_AdapterOptions as AdapterOptions,
	unstable_createAdapterProvider as createAdapterProvider,
	renderQueryString,
} from 'nuqs/adapters/custom';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

type Query = string | string[];

export type Emitter<Events extends Record<string, unknown>> = {
	on<Key extends keyof Events>(type: Key, handler: (event: Events[Key]) => any): () => void;
	off<Key extends keyof Events>(type: Key, handler?: (event: Events[Key]) => any): void;
	emit<Key extends keyof Events>(type: Key, event?: Events[Key] extends undefined ? never : Events[Key]): void;
};

function createEmitter<Events extends Record<string, unknown>>(): Emitter<Events> {
	const all = new Map<keyof Events, ((event: Events[keyof Events]) => any)[]>();
	return {
		on<Key extends keyof Events>(type: Key, handler: (event: Events[Key]) => any): () => void {
			const handlers = all.get(type) || [];
			handlers.push(handler as (event: Events[keyof Events]) => any);
			all.set(type, handlers);
			return () => this.off(type, handler);
		},
		off<Key extends keyof Events>(type: Key, handler: (event: Events[Key]) => any): void {
			const handlers = all.get(type);
			if (handlers) {
				all.set(
					type,
					handlers.filter(h => h !== handler)
				);
			}
		},
		emit<Key extends keyof Events>(type: Key, event?: Events[Key] extends undefined ? never : Events[Key]): void {
			const handlers = all.get(type);
			handlers?.forEach(handler => handler(event as Events[keyof Events]));
		},
	};
}

type SearchParamsSyncEmitterEvents = { update: URLSearchParams };

export function compareQuery<T extends Query>(a: T | null, b: T | null): boolean {
	if (a === b) {
		return true; // Referentially stable
	}
	if (a === null || b === null) {
		return false;
	}
	// we expect either strings or arrays, not a mix of both
	if (typeof a === 'string' || typeof b === 'string') {
		return false;
	}
	if (a.length !== b.length) {
		return false;
	}
	return a.every((value, index) => value === b[index]!);
}

export function applyChange(
	newValue: URLSearchParams,
	keys: string[],
	copy: boolean
): (oldValue: URLSearchParams) => URLSearchParams {
	return (oldValue: URLSearchParams) => {
		const hasChanged =
			keys.length === 0 ? true : keys.some(key => !compareQuery(oldValue.getAll(key), newValue.getAll(key)));
		if (!hasChanged) {
			return oldValue;
		}
		const filtered = filterSearchParams(newValue, keys, copy);
		return filtered;
	};
}

function filterSearchParams(search: URLSearchParams, keys: string[], copy: boolean): URLSearchParams {
	if (keys.length === 0) {
		return search;
	}
	const filtered = copy ? new URLSearchParams(search) : search;
	for (const key of search.keys()) {
		if (!keys.includes(key)) {
			filtered.delete(key);
		}
	}
	return filtered;
}

export function createReactRouterBasedAdapter(): {
	NuqsAdapter: ReturnType<typeof createAdapterProvider>;
} {
	const emitter = createEmitter<SearchParamsSyncEmitterEvents>();
	function useNuqsReactRouterBasedAdapter(watchKeys: string[]): AdapterInterface {
		const location = useLocation();
		const history = useHistory();
		const [searchParams, setSearchParams] = useState(() => {
			return filterSearchParams(new URLSearchParams(location.search), watchKeys, false);
		});
		useEffect(() => {
			setSearchParams(applyChange(new URLSearchParams(location.search), watchKeys, false));
			function onPopState(search: URLSearchParams) {
				setSearchParams(applyChange(search, watchKeys, false));
			}
			function onEmitterUpdate(search: URLSearchParams) {
				setSearchParams(applyChange(search, watchKeys, true));
			}
			emitter.on('update', onEmitterUpdate);
			const unlisten = history.listen((loc, action) => {
				if (action === 'POP') {
					onPopState(new URLSearchParams(loc.search));
				}
			});
			return () => {
				emitter.off('update', onEmitterUpdate);
				unlisten();
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [location.search, watchKeys.join('&')]);
		const updateUrl = (search: URLSearchParams, options: AdapterOptions) => {
			// First, update the URL locally without triggering a network request,
			// this allows keeping a reactive URL if the network is slow.
			const updateMethod = options.history === 'push' ? history.push : history.replace;
			updateMethod.call(history, location.pathname + renderQueryString(search), location.state);
			emitter.emit('update', search);
			if (options.scroll) {
				window.scrollTo(0, 0);
			}
		};
		const getSearchParamsSnapshot = useCallback(() => {
			return new URLSearchParams(location.search);
		}, [location.search]);
		return {
			searchParams,
			updateUrl,
			getSearchParamsSnapshot,
		};
	}

	return {
		NuqsAdapter: createAdapterProvider(useNuqsReactRouterBasedAdapter),
	};
}

const { NuqsAdapter } = createReactRouterBasedAdapter();
export const NuqsRR5Adapter = NuqsAdapter;
